import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/ToggleFiltersOpen";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { InvoiceHistoryPageContext } from "@insite/content-library/Pages/InvoiceHistoryPage";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    // currentState: state,
    billToState: getCurrentBillToState(state),
});

const mapDispatchToProps = {
    loadCurrentBillTo,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomInvoicePropertiesStyles {
    container?: GridContainerProps;
    heading?: TypographyProps;
    leftWrapper?: GridItemProps;
    rightWrapper?: GridItemProps;
    headingWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    labelHeading?: InjectableCss;
    labelData?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    noResultsText?: TypographyProps;
    noResultsContainer?: InjectableCss;
}

export const CustomInvoicePropertiesStyle: CustomInvoicePropertiesStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 10px;
        `,
    },
    leftWrapper: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-right: 4px;
                        `,
                    ],
                    "max",
                )}
            > div {
                width: 100%;
                height: 100%;
                border: 1px solid #cccccc;
            }
        `,
    },
    rightWrapper: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding-left: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 4px;
                        `,
                    ],
                    "max",
                )}
            > div {
                width: 100%;
                height: 100%;
                border: 1px solid #cccccc;
            }
        `,
    },
    headingWrapper: {
        css: css`
            text-align: center;
            font-size: 18px;
            font-weight: 600;
            background: #f8f9fa;
            border-bottom: 1px solid #cccccc;
            padding: 5px;
        `,
    },
    contentWrapper: {
        css: css`
            width: 100%;
            display: block;
            padding: 10px;
            > div {
                padding: 5px 0;
            }
        `,
    },
    labelHeading: {
        css: css`
            font-weight: bold;
        `,
    },
    labelData: {
        css: css`
            float: right;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
};

const styles = CustomInvoicePropertiesStyle;

const CustomInvoiceProperties: React.FC<Props> = ({ billToState, loadCurrentBillTo }) => {
    const invoicesDataView = useContext(InvoicesDataViewContext);
    let NewBalance;
    let pastDue;
    const commaValue = (x:any) => {
        if(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    };
    if(invoicesDataView.value) {
        NewBalance = commaValue(invoicesDataView.properties.newBalance);
        pastDue = commaValue(invoicesDataView.properties.pastDue);
    } else {
        NewBalance = "";
        pastDue = "";
    }

    if (billToState.isLoading || !billToState.value) {
        loadCurrentBillTo();
    }

    const companyName = billToState.value?.companyName;

    if (!invoicesDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }
    if (invoicesDataView.properties === undefined || invoicesDataView.properties === null) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No data found")}</Typography>
            </StyledWrapper>
        );
    }
    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.leftWrapper}>
                    <StyledWrapper>
                        <StyledWrapper {...styles.headingWrapper}>
                            <StyledWrapper>{companyName || "Company Name N/A"}</StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.contentWrapper}>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Terms")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.terms : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Credit Limit")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.creditLimit : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Available Credit")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.availableCredit : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Last Purchase")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.lastPurchase : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Last Payment")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.lastPayment : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("MTD Purchases")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.mtdPurchases : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("YTD Purchase")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.ytdPurchase : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Payment Days")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.paymentDays : ""}
                                </Typography>
                            </StyledWrapper>

                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("New Balance")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    <Typography>&#36;</Typography>
                                    {NewBalance}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Past Due")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    <Typography>&#36;</Typography>
                                    {pastDue}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightWrapper}>
                    <StyledWrapper>
                        <StyledWrapper {...styles.headingWrapper}>
                            <StyledWrapper>{translate("Open Balances")}</StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.contentWrapper}>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Future")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.future : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Current")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.current : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("31 - 60")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.thirty : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("61 - 90")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.sixty : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("91 - 120")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.ninety : ""}
                                </Typography>
                            </StyledWrapper>

                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Over 120")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.oneTwenty : ""}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="span" {...styles.labelHeading}>
                                    {translate("Total")}
                                </Typography>
                                <Typography as="span" {...styles.labelData}>
                                    {invoicesDataView.value ? invoicesDataView.properties.total : ""}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomInvoiceProperties),
    definition: {
        group: "Invoice History",
        displayName: "Custom Invoice Account Inquiry",
        // allowedContexts: [InvoiceHistoryPageContext],
    },
};

export default widgetModule;
